/**
 * Isolation Distribution Chart (Donut)
 *
 * Displays tenant distribution across isolation tiers as a donut chart.
 * Phase 3.5-B: Multi-Tenant Visibility
 */

import React from 'react';
import type { IsolationDistData } from '../../../features/dashboard/types/tenantHealth';

// Progressive purple scheme - matches HealthMatrix isolation badges
// Lighter = less isolation, darker = more isolation (NOT R/Y/G)
const TIER_COLORS: Record<string, string> = {
  row: '#818CF8',       // Light purple (least isolation)
  schema: '#6366F1',    // Medium purple
  container: '#4F46E5', // Dark purple (most isolation)
};

const TIER_LABELS: Record<string, string> = {
  row: 'Row',
  schema: 'Schema',
  container: 'Container',
};

export interface IsolationChartProps {
  data: IsolationDistData | null;
  isLoading: boolean;
}

/**
 * Renders a simple CSS-based donut chart for isolation distribution.
 */
export const IsolationChart: React.FC<IsolationChartProps> = ({ data, isLoading }) => {
  if (isLoading && !data) {
    return (
      <div className="card card-elevated" style={{ padding: 'var(--space-4)', minHeight: 200 }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
          Isolation Tier Distribution
        </h4>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-6)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card card-elevated" style={{ padding: 'var(--space-4)', minHeight: 200 }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
          Isolation Tier Distribution
        </h4>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-6)' }}>
          No data
        </div>
      </div>
    );
  }

  // Build conic-gradient segments
  const segments: string[] = [];
  let cumulative = 0;
  for (const tier of data.tiers) {
    const start = cumulative;
    const end = cumulative + tier.percentage;
    const color = TIER_COLORS[tier.isolation_type] || '#666';
    segments.push(`${color} ${start}% ${end}%`);
    cumulative = end;
  }
  // Fill remainder if rounding leaves a gap
  if (cumulative < 100 && segments.length > 0) {
    const lastColor = TIER_COLORS[data.tiers[data.tiers.length - 1]?.isolation_type] || '#666';
    segments.push(`${lastColor} ${cumulative}% 100%`);
  }

  const gradient = segments.length > 0
    ? `conic-gradient(${segments.join(', ')})`
    : 'conic-gradient(var(--bg-secondary) 0% 100%)';

  return (
    <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
      <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-md, 16px)', color: 'var(--text-primary)' }}>
        Isolation Tier Distribution
      </h4>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        {/* Donut */}
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: gradient,
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {/* Inner circle for donut hole */}
          <div
            style={{
              position: 'absolute',
              top: '25%',
              left: '25%',
              width: '50%',
              height: '50%',
              borderRadius: '50%',
              background: 'var(--bg-primary, #0f0f23)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-primary)',
              fontWeight: 700,
              fontSize: 'var(--text-lg, 18px)',
            }}
          >
            {data.total}
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {data.tiers.map((tier) => (
            <div key={tier.isolation_type} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  background: TIER_COLORS[tier.isolation_type] || '#666',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: 'var(--text-secondary)' }}>
                {TIER_LABELS[tier.isolation_type] || tier.isolation_type}
              </span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                {tier.count}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                ({tier.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IsolationChart;
